import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { get, post, api, fmtDT, fmtDate, useConfig } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/badges";
import { useEventDrawer } from "@/components/EventDrawer";

const TITLES = [["today", "Aujourd'hui"], ["new", "Nouveaux événements détectés"], ["modified", "Modifications"], ["cancelled", "Annulés / reportés"], ["economic", "Économie & entreprises — 7 prochains jours"], ["weak", "Signaux faibles"], ["to_verify", "À vérifier (7 prochains jours)"]];

function Settings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["digest-settings"], queryFn: () => get("/digest/settings") });
  const [f, setF] = useState(null);
  useEffect(() => { if (data) setF({ email: data.email || "", hour: data.hour, enabled: data.enabled }); }, [data]);
  if (!f) return null;
  const save = async () => {
    try {
      await api.put("/digest/settings", { ...f, hour: Number(f.hour) });
      toast.success("Paramètres du récap enregistrés");
      qc.invalidateQueries({ queryKey: ["digest-settings"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="panel p-4 space-y-3" data-testid="digest-settings">
      <h2 className="font-display font-bold flex items-center gap-1.5"><Mail className="h-4 w-4" />Envoi du récapitulatif</h2>
      <label className="flex items-center justify-between text-xs">Récap quotidien activé<Switch data-testid="digest-enabled" checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} /></label>
      <label className="block text-[11px] text-slate-500">Adresse e-mail destinataire
        <Input data-testid="digest-email" type="email" className="h-8 text-xs mt-0.5" placeholder="vous@exemple.fr" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></label>
      <label className="block text-[11px] text-slate-500">Heure d'envoi (heure de Paris)
        <select data-testid="digest-hour" className="w-full h-8 border border-slate-200 dark:border-slate-700 rounded-[3px] bg-transparent text-xs mt-0.5" value={f.hour} onChange={(e) => setF({ ...f, hour: e.target.value })}>
          {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
        </select></label>
      <Button data-testid="digest-save" size="sm" onClick={save}>Enregistrer</Button>
      <p className="text-[10px] text-slate-500">Sans adresse, le récap est généré uniquement dans l'application. Envoi via le service e-mail géré par Emergent (expéditeur « Radar Territorial »).</p>
    </div>
  );
}

const Section = ({ title, items }) => {
  const { open } = useEventDrawer();
  if (!items?.length) return null;
  return (
    <div className="panel">
      <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 font-display text-sm font-bold">{title} <span className="font-mono text-slate-500">({items.length})</span></div>
      {items.map((e) => (
        <button key={e.id} onClick={() => open(e.id)} className="w-full text-left px-3 py-1.5 border-b last:border-0 border-slate-100 dark:border-slate-800 text-xs flex gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors duration-150">
          <span className="font-mono text-[10px] text-slate-500 w-20 shrink-0">{fmtDate(e.next_date)} {e.start_time || ""}</span>
          <span className="flex-1 min-w-0"><span className="font-medium">{e.title}</span><span className="block text-[11px] text-slate-500 truncate">{e.commune_name} · {e.venue_name || "lieu inconnu"} · {e.organizer_name || "organisateur inconnu"} · {e.is_professional ? "Professionnel" : "Grand public"}</span></span>
          <StatusBadge status={e.status} />
        </button>
      ))}
    </div>
  );
};

export default function Recap() {
  const qc = useQueryClient();
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data: list } = useQuery({ queryKey: ["digests"], queryFn: () => get("/digests") });
  const { data: d } = useQuery({ queryKey: ["digest", sel], queryFn: () => get(sel ? `/digests/${sel}` : "/digests/latest") });
  const runNow = async () => {
    setBusy(true);
    const r = await post("/digests/run");
    setBusy(false);
    toast.success(`Récap généré — e-mail : ${r.email_status}`);
    setSel(null);
    qc.invalidateQueries({ queryKey: ["digests"] });
    qc.invalidateQueries({ queryKey: ["digest"] });
  };
  return (
    <div className="p-4 lg:p-6 grid grid-cols-1 [&>*]:min-w-0 xl:grid-cols-[1fr_340px] gap-5">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-xl font-bold">Récap du jour</h1>
          {d && <span className="font-mono text-xs text-slate-500" data-testid="recap-meta">généré le {fmtDT(d.created_at)} ({d.trigger}) · période depuis {fmtDT(d.since)} · e-mail : {d.email_status}</span>}
          <Button data-testid="recap-run-btn" size="sm" className="ml-auto h-7 text-xs" disabled={busy} onClick={runNow}><RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} />Générer maintenant</Button>
        </div>
        {!d && <div className="panel p-8 text-center text-xs text-slate-400">Aucun récap pour l'instant. Il sera généré automatiquement chaque matin, ou cliquez sur « Générer maintenant ».</div>}
        {d && (
          <>
            <div className="panel px-4 py-3 border-l-4 border-l-slate-900 dark:border-l-slate-100" data-testid="recap-summary">
              <div className="font-display text-lg font-bold">{d.counts.today} aujourd'hui · {d.counts.new} nouveaux · {d.counts.modified} modifiés · {d.counts.cancelled} annulés/reportés</div>
              <div className="text-xs text-slate-500">{d.counts.week} événements cette semaine · {d.counts.economic} économiques (7 j) · {d.counts.weak} signaux faibles · {d.counts.new_sources} nouvelles sources</div>
            </div>
            {TITLES.map(([k, t]) => <Section key={k} title={t} items={d.sections[k]} />)}
            {d.new_sources?.length > 0 && (
              <div className="panel"><div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 font-display text-sm font-bold">Nouvelles sources découvertes</div>
                {d.new_sources.map((s) => <a key={s.id} href={s.url} target="_blank" rel="noreferrer" className="block px-3 py-1 text-xs border-b last:border-0 border-slate-100 dark:border-slate-800 hover:underline">{s.name}</a>)}</div>
            )}
          </>
        )}
      </div>
      <aside className="space-y-4">
        <Settings />
        <div className="panel">
          <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 font-display text-sm font-bold">Récaps précédents</div>
          {(list || []).map((x) => (
            <button key={x.id} data-testid={`recap-item-${x.id}`} onClick={() => setSel(x.id)} className={`w-full text-left px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-xs hover:bg-slate-50 dark:hover:bg-slate-800/50 ${sel === x.id ? "bg-slate-100 dark:bg-slate-800" : ""}`}>
              <span className="font-mono text-[10px]">{fmtDT(x.created_at)}</span> · {x.counts.new} nouveaux · <span className="text-slate-500">{x.email_status}</span>
            </button>
          ))}
        </div>
      </aside>
    </div>
  );
}
